import {
  PAYMENT_BOOST_LANG_CODES,
  PAYMENT_BOOST_PAUSE_PERIODS,
  PAYMENT_BOOST_SID_CONFIG,
} from '@/base/const/paymentBoost.const'
import { CARD_TYPES, PAYWAY_GROUPS_TYPES } from '@/base/const/payway.const'
import { CookieClass } from '@/base/class/cookie.class'
import { DateClass } from '@/base/class/date.class'
import {
  COOKIE_CARD_DEPRECIATION_MECHANISM_ACTIVE,
  COOKIE_CARD_DEPRECIATION_MECHANISM_SHOW_GROUPS,
} from '@/base/const/cookies.const'
import { MATOMO_EVENT_CATEGORY, MATOMO_EVENT_TYPE } from '@/base/const/matomo.const'
import { MatomoClass } from '@/base/class/matomo.class'

export class PaymentBoost {
  /**
   * Sprawdzenie czy mechanizm payment boost ma być aktywny
   * @param {number} serviceID
   * @param {number} amount
   * @returns {boolean}
   */
  static isPaymentBoostActive(serviceID, amount) {
    if (!serviceID) {
      return false
    }

    // czasowe wyłączenie całego mechanizmu (np. na czas promocji kartowej)
    if (PaymentBoost.isPaymentBoostPaused()) {
      return false
    }

    const pbItem = PAYMENT_BOOST_SID_CONFIG.find((item) => Number(item.serviceId) === Number(serviceID))
    // jeśli SIDa nie ma na liście
    if (!pbItem) {
      return false
    }

    // jeśli kwota transkcji jest >= amount z konfiguracji
    if (Number(amount) >= Number(pbItem.transaction.amount)) {
      return true
    }

    // jeśli jeżyk przegladarki jest na liście, to stosujemy logikę optymalziacji
    const browserLang = navigator.language
    return PAYMENT_BOOST_LANG_CODES.includes(browserLang)
  }

  /**
   * Czy mechanizm payment boost jest w tej chwili w zaplanowanej przerwie
   * (`PAYMENT_BOOST_PAUSE_PERIODS`). Przerwa dotyczy wszystkich SIDów naraz.
   * @returns {boolean}
   */
  static isPaymentBoostPaused() {
    return PAYMENT_BOOST_PAUSE_PERIODS.some((period) => {
      // wpis bez granic wyłączyłby mechanizm bezterminowo — traktujemy go jak błąd konfiguracji
      if (!period?.start && !period?.end) {
        return false
      }
      return DateClass.isDateActive(period.start ?? null, period.end ?? null)
    })
  }

  /**
   * Czy grupa to promocja kartowa (PROMO z kanałem 1500) — traktowana jak grupa kartowa
   * @param {object} group
   * @returns {boolean}
   */
  static isCardPromoGroup(group) {
    if (group?.type !== PAYWAY_GROUPS_TYPES.promo) {
      return false
    }
    return !!group.payways?.some((payway) => payway.id === CARD_TYPES.creditCard.id)
  }

  /**
   * Czy grupa jest chowana przez payment boost do czasu kliknięcia "pokaż więcej"
   * @param {object} group
   * @returns {boolean}
   */
  static isGroupHiddenByBoost(group) {
    return (
      [
        PAYWAY_GROUPS_TYPES.card,
        PAYWAY_GROUPS_TYPES.wallet,
        PAYWAY_GROUPS_TYPES.googlePay,
        PAYWAY_GROUPS_TYPES.applePay,
        PAYWAY_GROUPS_TYPES.visaMobile,
      ].includes(group?.type) || PaymentBoost.isCardPromoGroup(group)
    )
  }

  /**
   * Metoda sortująca wygenerowaną grupę list wg logiki payment boost (grupy kartowe na dół)
   * @param {array} groups
   * @returns {*[]}
   */
  static sortPaymentGroups(groups = []) {
    let result = [...groups]

    // szukamy grupy PROMO z kanałem 1500 i przesuwamy je na koniec
    const promoIdx = result.findIndex((group) => PaymentBoost.isCardPromoGroup(group))
    if (promoIdx !== -1) {
      result = [...result.slice(0, promoIdx), ...result.slice(promoIdx + 1), result[promoIdx]]
    }

    // przesuwamy na koniec: CARDS, WALLET, GOOGLE_PAY, APPLE_PAY, VISAMOBILE
    for (const type of [
      PAYWAY_GROUPS_TYPES.card,
      PAYWAY_GROUPS_TYPES.wallet,
      PAYWAY_GROUPS_TYPES.googlePay,
      PAYWAY_GROUPS_TYPES.applePay,
      PAYWAY_GROUPS_TYPES.visaMobile,
    ]) {
      const idx = result.findIndex((group) => group.type === type)
      if (idx !== -1) {
        result = [...result.slice(0, idx), ...result.slice(idx + 1), result[idx]]
      }
    }

    return result
  }

  static canSendCardDepreciationMechanismActiveEvent(transRemoteID) {
    return PaymentBoost._canSendEvent(COOKIE_CARD_DEPRECIATION_MECHANISM_ACTIVE, transRemoteID)
  }

  static sendCardDepreciationMechanismActiveEvent(serviceId, transRemoteID) {
    PaymentBoost._sendEvent(
      COOKIE_CARD_DEPRECIATION_MECHANISM_ACTIVE,
      'card_depreciation_mechanism_active',
      serviceId,
      transRemoteID
    )
  }

  static canSendCardDepreciationMechanismShowGroupsEvent(transRemoteID) {
    return PaymentBoost._canSendEvent(COOKIE_CARD_DEPRECIATION_MECHANISM_SHOW_GROUPS, transRemoteID)
  }

  static sendCardDepreciationMechanismShowGroupsEvent(serviceId, transRemoteID) {
    PaymentBoost._sendEvent(
      COOKIE_CARD_DEPRECIATION_MECHANISM_SHOW_GROUPS,
      'card_depreciation_mechanism_show_groups',
      serviceId,
      transRemoteID
    )
  }

  static _canSendEvent(cookieName, transRemoteID) {
    const cookie = CookieClass.getCookie(cookieName)
    return !(cookie && cookie === String(transRemoteID))
  }

  static _sendEvent(cookieName, eventName, serviceId, transRemoteID) {
    MatomoClass.sendEvent([
      MATOMO_EVENT_TYPE.trackEvent,
      MATOMO_EVENT_CATEGORY.costOptimization,
      eventName,
      Number(serviceId),
      String(transRemoteID),
    ])
    CookieClass.setCookie(cookieName, String(transRemoteID), 1)
  }
}
